// @flow

import React from 'react';
import { Translate } from 'react-redux-i18n';
import RoiKpiPopinDetails from './RoiKpiPopinDetails';
import {
  computePayback,
  formatMgr,
  formatPayback,
  formatExitCap,
  formatRoi,
  formatRoiParam,
  formatTic,
} from '../../../../utils/formatUtils';
import type { RoiResult } from '../../../../types/apolloType';

type Props = {
  roiSuggestionResult: RoiResult,
  focus: string,
}

function RoiSuggestionResultDetails(props: Props) {
  const roi = formatRoi(props.roiSuggestionResult.roi);
  const payback = formatPayback(computePayback(
    props.roiSuggestionResult.netMgrGlaUplift,
    props.roiSuggestionResult.tic,
  ));
  const mgr = formatMgr(props.roiSuggestionResult.netMgrGlaUplift);
  const isMgrPositive = props.roiSuggestionResult.netMgrGlaUplift > 0;
  const tic = formatTic(props.roiSuggestionResult.tic);
  const keyMoney = formatTic(props.roiSuggestionResult.roiParams.keyMoney);
  const isKeyMoneyPositive = props.roiSuggestionResult.roiParams.keyMoney > 0;
  const incentives = formatTic(props.roiSuggestionResult.roiParams.rentIncentives);
  const isIncentivesPositive = props.roiSuggestionResult.roiParams.rentIncentives > 0;
  const valueCreationVsCurrent = formatRoiParam(props.roiSuggestionResult.valueCreationVsCurrent);
  const newMgrGlaNet = formatRoiParam(props.roiSuggestionResult.roiParams.newMgrGlaNet);
  const oldMgrGlaNet = formatRoiParam(props.roiSuggestionResult.roiParams.oldMgrGlaNet);
  const evictionCost = formatRoiParam(props.roiSuggestionResult.roiParams.evictionCost);
  const leasingFees = formatRoiParam(props.roiSuggestionResult.roiParams.leasingFees);
  const lossOfRent = formatRoiParam(props.roiSuggestionResult.roiParams.lossOfRent);
  const vacancyCharges = formatRoiParam(props.roiSuggestionResult.roiParams.vacancyCharges);
  const foc = formatRoiParam(props.roiSuggestionResult.roiParams.foc);
  const exitCap = formatExitCap(props.roiSuggestionResult.roiParams.exitCap);
  const transferTax =
    formatExitCap(props.roiSuggestionResult.roiParams.transferTaxAndTransactionCosts);
  const fra = formatRoiParam(props.roiSuggestionResult.roiParams.fra);
  const pal = formatRoiParam(props.roiSuggestionResult.roiParams.pal);

  return (
    <div className="roi-result-modal">
      <div className="roi-result-detail">
        <div className="roi-result-detail-title">
          <RoiKpiPopinDetails
            kpiValue={roi}
            label={<Translate value="roi.kpi.roi" />}
            isFocus={false}
            hideInfoBox
          />
        </div>
        <RoiKpiPopinDetails kpiValue="=" label=" " isFocus={false} hideInfoBox />
        <RoiKpiPopinDetails kpiValue={mgr} label={<Translate value="roi.kpi.mgr" />} isFocus={false} hideInfoBox />
        <RoiKpiPopinDetails kpiValue="/" label=" " isFocus={false} hideInfoBox />
        <RoiKpiPopinDetails kpiValue={tic} label={<Translate value="roi.kpi.tic" />} isFocus={false} hideInfoBox />
        <RoiKpiPopinDetails kpiValue="*" label=" " isFocus={false} hideInfoBox />
        <RoiKpiPopinDetails kpiValue="100" label={<Translate value="roi.toPercentage" />} isFocus={false} hideInfoBox />
      </div>
      <div className="roi-result-detail">
        <div className="roi-result-detail-title">
          <RoiKpiPopinDetails
            kpiValue={payback}
            label={<Translate value="roi.kpi.payback" />}
            isFocus={false}
            hideInfoBox={false}
          />
        </div>
        <RoiKpiPopinDetails kpiValue="=" label=" " isFocus={false} hideInfoBox />
        <RoiKpiPopinDetails kpiValue={tic} label={<Translate value="roi.kpi.tic" />} isFocus={false} hideInfoBox />
        <RoiKpiPopinDetails kpiValue="/" label=" " isFocus={false} hideInfoBox />
        <RoiKpiPopinDetails kpiValue={mgr} label={<Translate value="roi.kpi.mgr" />} isFocus={false} hideInfoBox />
      </div>
      <div className="roi-result-detail">
        <div className="roi-result-detail-title">
          <RoiKpiPopinDetails
            kpiValue={mgr}
            label={<Translate value="roi.kpi.mgr" />}
            isFocus={props.focus === 'mgr'}
            isPositive={isMgrPositive}
            hideInfoBox={false}
          />
        </div>
        <RoiKpiPopinDetails kpiValue="=" label=" " isFocus={false} hideInfoBox />
        <RoiKpiPopinDetails kpiValue={newMgrGlaNet} label={<Translate value="roi.kpi.newMgrGlaNet" />} isFocus={false} hideInfoBox />
        <RoiKpiPopinDetails kpiValue="-" label=" " isFocus={false} hideInfoBox />
        <RoiKpiPopinDetails kpiValue={oldMgrGlaNet} label={<Translate value="roi.kpi.oldMgrGlaNet" />} isFocus={false} hideInfoBox />
      </div>
      <div className="roi-result-detail roi-result-detail-tic">
        <div className="roi-result-detail-title">
          <RoiKpiPopinDetails
            kpiValue={tic}
            label={<Translate value="roi.kpi.tic" />}
            isFocus={false}
            hideInfoBox={false}
          />
        </div>
        <div className="roi-result-detail roi-result-detail-tic--calculations">
          <RoiKpiPopinDetails kpiValue="= -" label=" " isFocus={false} hideInfoBox />
          <RoiKpiPopinDetails kpiValue={keyMoney} label={<Translate value="roi.kpi.keyMoney" />} isFocus={false} hideInfoBox />
          <RoiKpiPopinDetails kpiValue="+" label=" " isFocus={false} hideInfoBox />
          <RoiKpiPopinDetails kpiValue={evictionCost} label={<Translate value="roi.params.evictionCost" />} isFocus={false} hideInfoBox />
          <RoiKpiPopinDetails kpiValue="+" label=" " isFocus={false} hideInfoBox />
          <RoiKpiPopinDetails kpiValue={foc} label={<Translate value="roi.params.foc" />} isFocus={false} hideInfoBox />
          <RoiKpiPopinDetails kpiValue="+" label=" " isFocus={false} hideInfoBox />
          <RoiKpiPopinDetails kpiValue={incentives} label={<Translate value="roi.kpi.Incentives" />} isFocus={false} hideInfoBox />
          <RoiKpiPopinDetails kpiValue="+" label=" " isFocus={false} hideInfoBox />
          <RoiKpiPopinDetails kpiValue={leasingFees} label={<Translate value="roi.category.leasingFees" />} isFocus={false} hideInfoBox />
          <RoiKpiPopinDetails kpiValue="+" label=" " isFocus={false} hideInfoBox />
          <RoiKpiPopinDetails kpiValue={lossOfRent} label={<Translate value="roi.params.lossOfRent" />} isFocus={false} hideInfoBox />
          <RoiKpiPopinDetails kpiValue="+" label=" " isFocus={false} hideInfoBox />
          <RoiKpiPopinDetails kpiValue={vacancyCharges} label={<Translate value="roi.params.vacancyCharges" />} isFocus={false} hideInfoBox />
        </div>
      </div>
      <div className="roi-result-detail">
        <div className="roi-result-detail-title">
          <RoiKpiPopinDetails
            kpiValue={keyMoney}
            label={<Translate value="roi.kpi.keyMoney" />}
            isFocus={props.focus === 'keymoney'}
            isPositive={isKeyMoneyPositive}
            hideInfoBox={false}
          />
        </div>
        <RoiKpiPopinDetails kpiValue="= avg(DEN)" label=" " isFocus={false} hideInfoBox />
      </div>
      <div className="roi-result-detail">
        <div className="roi-result-detail-title">
          <RoiKpiPopinDetails
            kpiValue={valueCreationVsCurrent}
            label={<Translate value="roi.kpi.valueCreationVsCurrent" />}
            isFocus={false}
            hideInfoBox={false}
          />
        </div>
        <RoiKpiPopinDetails kpiValue="= (" label=" " isFocus={false} hideInfoBox />
        <RoiKpiPopinDetails kpiValue={mgr} label={<Translate value="roi.kpi.mgr" />} isFocus={false} hideInfoBox />
        <RoiKpiPopinDetails kpiValue="/ (" label=" " isFocus={false} hideInfoBox />
        <RoiKpiPopinDetails kpiValue={exitCap} label={<Translate value="roi.params.exitCap" />} isFocus={false} hideInfoBox />
        <RoiKpiPopinDetails kpiValue="-" label=" " isFocus={false} hideInfoBox />
        <RoiKpiPopinDetails kpiValue={tic} label={<Translate value="roi.kpi.tic" />} isFocus={false} hideInfoBox />
        <RoiKpiPopinDetails kpiValue=") ) / ( 1 +" label=" " isFocus={false} hideInfoBox />
        <RoiKpiPopinDetails kpiValue={transferTax} label={<Translate value="roi.params.transferTaxAndTransactionCosts" />} isFocus={false} hideInfoBox />
        <RoiKpiPopinDetails kpiValue=")" label=" " isFocus={false} hideInfoBox />
      </div>
      <div className="roi-result-detail">
        <div className="roi-result-detail-title">
          <RoiKpiPopinDetails
            kpiValue={incentives}
            label={<Translate value="roi.kpi.Incentives" />}
            isFocus={props.focus === 'incentives'}
            isPositive={isIncentivesPositive}
            hideInfoBox={false}
          />
        </div>
        <RoiKpiPopinDetails kpiValue="=" label=" " isFocus={false} hideInfoBox />
        <RoiKpiPopinDetails kpiValue={fra} label={<Translate value="roi.params.fra" />} isFocus={false} hideInfoBox />
        <RoiKpiPopinDetails kpiValue="+" label=" " isFocus={false} hideInfoBox />
        <RoiKpiPopinDetails kpiValue={pal} label={<Translate value="roi.params.pal" />} isFocus={false} hideInfoBox />
      </div>
    </div>
  );
}

export default RoiSuggestionResultDetails;
